import { useTranslation } from 'react-i18next';

import { VIBRATO_REFERENCE_HZ } from '@muzly/voice-engine';

import { MiniWave, VibratoWaveChart } from './charts';
import { ItemRow } from './ResultRow';
import { Card } from '../../../../components';

import type { DiagnosisRecord } from '../../record';

/**
 * 떨림(바이브레이션) — 파형 · 속도/폭/규칙성 세 칸 · 규칙성 슬라이더 · 폭 설명(시안 §8.6
 * `994:7699`).
 *
 * **점수가 아니라 실측 세 값이다.** 탭 머리의 `vibrato` 점수(0~100)는 이 값 중 속도·폭을
 * 참고 구간에 댄 판정이고(`vibratoAxisScore`), 여기는 목소리가 실제로 무엇을 했는지다.
 *
 * 못 잰 회차는 **세 값이 통째로 없다** — 0 Hz·0 cents는 "떨림이 없었다"로 읽히는데,
 * 실제로는 이어진 구간이 짧아 셀 주기가 없었던 것이다(`analyzeVibrato`가 null을 낸다).
 * 시안의 `상위 75%` 배지는 비교할 모집단이 없어 그리지 않는다.
 */

/** 한 반음 = 100 cents. 폭을 반음 대비로 말해야 "±58 cents"가 몸으로 읽힌다. */
const CENTS_PER_SEMITONE = 100;

const Stat = ({
  label,
  value,
  unit,
  note,
}: {
  label: string;
  value: string;
  unit: string;
  note: string;
}) => (
  <div className="flex flex-col items-center gap-3">
    <span className="text-[13px] font-semibold text-bk-600">{label}</span>
    <div className="flex flex-col items-center gap-2">
      <p className="flex items-end gap-1 leading-none">
        <span className="text-xl font-bold tabular-nums text-bk-900">
          {value}
        </span>
        <span className="text-sm font-medium text-bk-600">{unit}</span>
      </p>
      <span className="whitespace-nowrap text-xs font-medium text-bk-800">
        {note}
      </span>
    </div>
  </div>
);

const EXPLAIN = [
  { id: 'small', amplitude: 4 },
  { id: 'good', amplitude: 9 },
  { id: 'large', amplitude: 16 },
] as const;

export const VibratoSection = ({ record }: { record: DiagnosisRecord }) => {
  const { t } = useTranslation();
  const vibrato = record.vibrato;
  const pending = t('web.result.pending');

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-10 pb-4 pt-6">
        {/* 파형(시안 §8.6)은 그 회차의 떨림이거나(음정 곡선이 있을 때) 잰 두 값으로 그린
            모식도다 — 떨림을 못 잰 회차에는 그릴 근거가 없으므로 아무것도 안 그린다. */}
        {vibrato ? (
          <VibratoWaveChart
            rateHz={vibrato.rateHz}
            depthCents={vibrato.depthCents}
            regularityPct={vibrato.regularityPct}
            series={record.pitchSeries}
          />
        ) : (
          <p className="text-center text-sm font-semibold text-bk-600">
            {pending}
          </p>
        )}

        <div className="mx-[18px] flex flex-col items-center gap-8">
          <div className="flex items-start justify-center gap-8">
            <Stat
              label={t('web.result.vibrato.rate')}
              value={vibrato ? vibrato.rateHz.toFixed(1) : '—'}
              unit="Hz"
              note={t('web.result.vibrato.rateRef', VIBRATO_REFERENCE_HZ)}
            />
            <Stat
              label={t('web.result.vibrato.depth')}
              value={vibrato ? `±${Math.round(vibrato.depthCents)}` : '—'}
              unit={t('web.result.vibrato.centsUnit')}
              note={
                vibrato
                  ? t('web.result.vibrato.depthRatio', {
                      ratio: (vibrato.depthCents / CENTS_PER_SEMITONE).toFixed(
                        1,
                      ),
                    })
                  : pending
              }
            />
            <Stat
              label={t('web.result.vibrato.regularity')}
              value={vibrato ? String(Math.round(vibrato.regularityPct)) : '—'}
              unit="%"
              note={t('web.result.vibrato.regularityBasis')}
            />
          </div>

          {/* 규칙성 슬라이더 — 낮음·보통·좋음. 값이 없으면 마커도 없다. */}
          {vibrato && (
            <div className="w-full max-w-[303px]" aria-hidden="true">
              <div className="relative h-2.5 rounded-full bg-bk-100">
                <span
                  className="absolute inset-y-0 left-0 rounded-full bg-primary"
                  style={{ width: `${vibrato.regularityPct}%` }}
                />
                <span
                  className="absolute top-1/2 h-[18px] w-[18px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary ring-[3px] ring-white shadow-chip"
                  style={{ left: `${vibrato.regularityPct}%` }}
                />
              </div>
              <div className="mt-2 flex justify-between text-[12.5px] font-medium text-bk-600">
                <span>{t('web.result.vibrato.regularityScale.low')}</span>
                <span>{t('web.result.vibrato.regularityScale.mid')}</span>
                <span>{t('web.result.vibrato.regularityScale.high')}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-[18px] py-4">
        <Card className="mx-[18px] px-5 py-[18px]">
          <h3 className="text-lg font-semibold tracking-[-0.09px] text-bk-900">
            {t('web.result.vibrato.explain.title')}
          </h3>
          <p className="mt-2 text-sm leading-[1.35] text-bk-800">
            {t('web.result.vibrato.explain.body')}
          </p>
        </Card>
        <div className="flex flex-col gap-2 px-0.5">
          {EXPLAIN.map(({ id, amplitude }) => (
            <ItemRow
              key={id}
              title={t(`web.result.vibrato.explain.${id}.title`)}
              desc={t(`web.result.vibrato.explain.${id}.desc`)}
              right={<MiniWave amplitude={amplitude} />}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
